import React, { useState, useEffect, useRef } from "react";
import Papa from "papaparse";
import {
  getFirestore,
  writeBatch,
  doc,
  collection,
  getDocs,
  where,
  orderBy,
  query,
  limit,
  updateDoc,
} from "firebase/firestore";
import { Helmet } from "react-helmet";
import { ChevronDown, Plus, Settings, FileText, Database } from "lucide-react";

// Enhanced component with CSV migration and bulk field update features
const EnhancedMigrationTool = () => {
  // State for the original CSV migration tool
  const [results, setResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  // New state for the bulk update feature
  const [activeTab, setActiveTab] = useState("csv");
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [documentCount, setDocumentCount] = useState(0);
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("string");
  const [fieldValue, setFieldValue] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(null);
  const [updateResults, setUpdateResults] = useState(null);
  const [confirmUpdate, setConfirmUpdate] = useState(false);

  /* ─── Back‑fill tab state ───────────────────────── */
  const [backfillMode, setBackfillMode] = useState("manual"); // "manual" or "auto"
  const [processingMode, setProcessingMode] = useState("single");
  const [allResidents, setAllResidents] = useState([]);
  const [backfillResults, setBackfillResults] = useState(null);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [boolFields, setBoolFields] = useState({
    homelessOneYear: "",
    childhoodTrauma: "",
    twoParentsHome: "",
    coOccurringDiagnosis: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Field mappings and other configuration from the original component
  const fieldMappings = {
    first_name: "firstName",
    last_name: "lastName",
    birthdate: "dob",
    phone: "phoneNumber",
    facility: "house",
    admitted_at: "intakeDate",
    discharged_at: "dischargeDate",
    latest_sobriety_date: "sobrietyDate",
    referrer_name: "referralLocation",
    veteran_status: "veteranStatus",
    status: "currentResident",
    emergency_contact_1_name: "ecFirstName",
    emergency_contact_1_phone: "ecPhoneNumber",
    city: "city",
    employment_start_date: "employmentStartDate",
    employment_end_date: "employmentEndDate",
    race: "race",
    email: "email",
    ethnicity: "ethnicity",
    mental_health_diagnosis: "mentalHealthDiagnosis",
    gender: "gender",
    marital_status: "maritalStatus",
  };

  // Default values for missing fields
  const defaultValues = {
    firstName: "",
    lastName: "",
    fullName: "",
    dob: "",
    phoneNumber: "",
    house: "",
    rentAmount: 0,
    rentSchedule: "weekly",
    rentDueDate: null,
    intakeDate: "",
    ecFirstName: "",
    ecLastName: "",
    ecRelationship: "",
    ecPhoneNumber: "",
    medications: [],
    drugOfChoice: [],
    sobrietyDate: "",
    referralLocation: "",
    poFirstName: "",
    poLastName: "",
    county: "",
    pendingCharges: "",
    isMAT: false,
    balance: 0,
    currentResident: true,
    bedLocation: "",
    uaCount: 3,
    gender: "",
    veteranStatus: "",
    dischargeDate: "",
    dischargeStatus: "",
    dischargeNotes: "",
    sobrietyStatus: "Unknown",
    employmentStartDate: "",
    employmentEndDate: "",
    race: "",
    email: "",
    ethnicity: "",
    mentalHealthDiagnosis: "",
    maritalStatus: "",
  };

  // Fetch organizations when component mounts
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Fetch collections when an organization is selected
  useEffect(() => {
    if (selectedOrg && activeTab === "bulk") {
      fetchCollections(selectedOrg.id);
    }
  }, [selectedOrg, activeTab]);

  /* ─── Fetch residents for Back‑fill tab ───────────────── */
  useEffect(() => {
    if (!selectedOrg || activeTab !== "backfill") return;

    (async () => {
      try {
        const db = getFirestore();

        /* active residents */
        const resRef = collection(
          db,
          `organizations/${selectedOrg.id}/residents`
        );

        /* alumni residents  (↳ adjust name if yours differs) */
        const alumniRef = collection(
          db,
          `organizations/${selectedOrg.id}/alumni`
        );

        const [resSnap, alumniSnap] = await Promise.all([
          getDocs(resRef),
          getDocs(alumniRef),
        ]);

        // For the OLD manual tool (residents state)
        const list = [...resSnap.docs, ...alumniSnap.docs].map((d) => {
          const data = d.data();
          const fullName =
            data.fullName ||
            `${data.firstName || ""} ${data.lastName || ""}`.trim();
          return {
            id: d.id,
            fullName,
            isAlumni: alumniSnap.docs.some((al) => al.id === d.id), // simple flag
          };
        });

        /* optional: sort alphabetically */
        list.sort((a, b) =>
          a.fullName.localeCompare(b.fullName, undefined, {
            sensitivity: "base",
          })
        );

        setResidents(list); // For your existing manual tool

        // For the NEW automatic tool (allResidents state)
        const residents = resSnap.docs.map((doc) => ({
          id: doc.id,
          fullName:
            doc.data().fullName ||
            `${doc.data().firstName || ""} ${doc.data().lastName || ""}`.trim(),
          collection: "residents",
          data: doc.data(),
        }));

        const alumni = alumniSnap.docs.map((doc) => ({
          id: doc.id,
          fullName:
            doc.data().fullName ||
            `${doc.data().firstName || ""} ${doc.data().lastName || ""}`.trim(),
          collection: "alumni",
          data: doc.data(),
        }));

        const allPeople = [...residents, ...alumni].sort((a, b) =>
          a.fullName.localeCompare(b.fullName, undefined, {
            sensitivity: "base",
          })
        );

        setAllResidents(allPeople); // For your new automatic tool
      } catch (err) {
        console.error("Error loading residents:", err);
        setError("Failed to load residents");
      }
    })();
  }, [selectedOrg, activeTab]);

  /* ─── Update every Recovery‑Capital doc for this resident ───────────── */
  const runBackfill = async () => {
    if (!selectedOrg || !selectedResident) {
      setError("Select an organization and resident first");
      return;
    }

    const payload = Object.fromEntries(
      Object.entries(boolFields).filter(([_, v]) => v) // keep only chosen values
    );
    if (Object.keys(payload).length === 0) {
      setError("Choose at least one Yes/No value");
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);
      const db = getFirestore();

      /* paths */
      const nestedPath = `organizations/${selectedOrg.id}/residents/${selectedResident.id}/recoveryCapital`;
      const topPath = `organizations/${selectedOrg.id}/recoveryCapital`;

      /* grab docs */
      const [nestedSnap, topSnap] = await Promise.all([
        getDocs(collection(db, nestedPath)),
        getDocs(
          query(
            collection(db, topPath),
            where("residentName", "==", selectedResident.fullName)
          )
        ),
      ]);

      const docs = [...nestedSnap.docs, ...topSnap.docs];
      const BATCH = 400;
      for (let i = 0; i < docs.length; i += BATCH) {
        const batch = writeBatch(db);
        docs.slice(i, i + BATCH).forEach((d) => batch.update(d.ref, payload));
        await batch.commit();
      }

      setUpdateSuccess({ backfilled: docs.length });
    } catch (err) {
      console.error("Back‑fill error:", err);
      setError(`Back‑fill failed: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const loadAllResidents = async () => {
    if (!selectedOrg) return;

    try {
      const db = getFirestore();

      // Fetch from both collections
      const [resSnap, alumniSnap] = await Promise.all([
        getDocs(collection(db, `organizations/${selectedOrg.id}/residents`)),
        getDocs(collection(db, `organizations/${selectedOrg.id}/alumni`)),
      ]);

      const residents = resSnap.docs.map((doc) => ({
        id: doc.id,
        fullName:
          doc.data().fullName ||
          `${doc.data().firstName || ""} ${doc.data().lastName || ""}`.trim(),
        collection: "residents",
        data: doc.data(),
      }));

      const alumni = alumniSnap.docs.map((doc) => ({
        id: doc.id,
        fullName:
          doc.data().fullName ||
          `${doc.data().firstName || ""} ${doc.data().lastName || ""}`.trim(),
        collection: "alumni",
        data: doc.data(),
      }));

      const allPeople = [...residents, ...alumni].sort((a, b) =>
        a.fullName.localeCompare(b.fullName, undefined, { sensitivity: "base" })
      );

      setAllResidents(allPeople);
    } catch (err) {
      console.error("Error loading residents:", err);
      setError("Failed to load residents and alumni");
    }
  };

  // Fetch all organizations from Firestore
  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const db = getFirestore();
      const orgsRef = collection(db, "organizations");
      const snapshot = await getDocs(orgsRef);

      const orgsData = [];

      for (const orgDoc of snapshot.docs) {
        try {
          // Get organization name from houseSettings if available
          let orgName = orgDoc.id;

          try {
            const houseSettingsRef = doc(
              db,
              "organizations",
              orgDoc.id,
              "houseSettings",
              "initial"
            );
            const houseSettingsSnap = await getDocs(houseSettingsRef);
            if (
              houseSettingsSnap.exists() &&
              houseSettingsSnap.data()?.organizationName
            ) {
              orgName = houseSettingsSnap.data().organizationName;
            }
          } catch (error) {
            console.warn(`Could not get name for org ${orgDoc.id}:`, error);
          }

          orgsData.push({
            id: orgDoc.id,
            name: orgName,
          });
        } catch (error) {
          console.error(`Error processing org ${orgDoc.id}:`, error);
        }
      }

      setOrganizations(orgsData);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setError("Failed to load organizations. Please refresh the page.");
    } finally {
      setLoadingOrgs(false);
    }
  };

  // Fetch collections from the selected organization
  const fetchCollections = async (orgId) => {
    try {
      setLoadingCollections(true);
      setCollections([]);
      setSelectedCollection(null);

      const db = getFirestore();
      const orgPath = `organizations/${orgId}`;

      // Get all collections under this organization
      // Note: This is a limited approach as Firestore doesn't have a native "list collections" API
      // Common collections to check
      const commonCollections = [
        "residents",
        "houses",
        "payments",
        "uaTests",
        "notes",
      ];

      const collectionsData = [];

      for (const collName of commonCollections) {
        try {
          const collRef = collection(db, orgPath, collName);
          const testQuery = query(collRef, limit(1));
          const testSnapshot = await getDocs(testQuery);

          if (!testSnapshot.empty) {
            collectionsData.push({
              id: collName,
              name: collName.charAt(0).toUpperCase() + collName.slice(1), // Capitalize first letter
              path: `${orgPath}/${collName}`,
            });
          }
        } catch (error) {
          console.warn(`Collection ${collName} not found or error:`, error);
        }
      }

      setCollections(collectionsData);
    } catch (error) {
      console.error("Error fetching collections:", error);
      setError("Failed to load collections.");
    } finally {
      setLoadingCollections(false);
    }
  };

  // Function to extract risk factors from questionnaire data
  const extractRiskFactors = (questionnaireData) => {
    const riskFactors = {};
    const riskFactorFields = [
      "overOneYearInPrison",
      "homelessOneYear",
      "childhoodTrauma",
      "twoParentsHome",
      "coOccurringDiagnosis",
      "kids",
    ];

    riskFactorFields.forEach((field) => {
      if (
        questionnaireData[field] &&
        (questionnaireData[field] === "Yes" ||
          questionnaireData[field] === "No")
      ) {
        riskFactors[field] = questionnaireData[field];
      }
    });

    return riskFactors;
  };

  const getLatestQuestionnaire = async (residentName, residentId) => {
    const db = getFirestore();

    try {
      // Check both organization-level and resident-specific collections
      const [orgLevelSnap, residentSpecificSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, `organizations/${selectedOrg.id}/recoveryCapital`),
            where("residentName", "==", residentName),
            orderBy("timestamp", "desc"),
            limit(1)
          )
        ),
        getDocs(
          query(
            collection(
              db,
              `organizations/${selectedOrg.id}/residents/${residentId}/recoveryCapital`
            ),
            orderBy("timestamp", "desc"),
            limit(1)
          )
        ),
      ]);

      // Get the most recent questionnaire from either collection
      let latestDoc = null;
      let latestTimestamp = null;

      if (!orgLevelSnap.empty) {
        const orgDoc = orgLevelSnap.docs[0];
        const orgTimestamp = orgDoc.data().timestamp;
        if (
          !latestTimestamp ||
          (orgTimestamp && orgTimestamp.toMillis() > latestTimestamp.toMillis())
        ) {
          latestDoc = orgDoc;
          latestTimestamp = orgTimestamp;
        }
      }

      if (!residentSpecificSnap.empty) {
        const resDoc = residentSpecificSnap.docs[0];
        const resTimestamp = resDoc.data().timestamp;
        if (
          !latestTimestamp ||
          (resTimestamp && resTimestamp.toMillis() > latestTimestamp.toMillis())
        ) {
          latestDoc = resDoc;
          latestTimestamp = resTimestamp;
        }
      }

      return latestDoc ? latestDoc.data() : null;
    } catch (err) {
      console.error(`Error getting questionnaire for ${residentName}:`, err);
      return null;
    }
  };

  // Function to update a single resident's risk factors
  const updateResidentRiskFactors = async (resident) => {
    try {
      // Get the latest questionnaire
      const latestQuestionnaire = await getLatestQuestionnaire(
        resident.fullName,
        resident.id
      );

      if (!latestQuestionnaire) {
        return { success: false, reason: "No questionnaire found" };
      }

      // Extract risk factors
      const riskFactors = extractRiskFactors(latestQuestionnaire);

      if (Object.keys(riskFactors).length === 0) {
        return {
          success: false,
          reason: "No risk factors found in questionnaire",
        };
      }

      // Update the resident document
      const db = getFirestore();
      const docRef = doc(
        db,
        `organizations/${selectedOrg.id}/${resident.collection}/${resident.id}`
      );

      await updateDoc(docRef, riskFactors);

      return {
        success: true,
        updatedFields: riskFactors,
        questionnaire: latestQuestionnaire.questionnaire || "Unknown",
      };
    } catch (err) {
      console.error(`Error updating ${resident.fullName}:`, err);
      return { success: false, reason: err.message };
    }
  };

  // Single resident backfill
  const backfillSingleResident = async () => {
    if (!selectedResident) {
      setError("Please select a resident");
      return;
    }

    setIsBackfilling(true);
    setError(null);
    setBackfillResults(null);

    try {
      const result = await updateResidentRiskFactors(selectedResident);
      setBackfillResults([
        {
          resident: selectedResident,
          ...result,
        },
      ]);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsBackfilling(false);
    }
  };

  // Bulk backfill for all residents
  const backfillAllResidents = async () => {
    if (allResidents.length === 0) {
      setError("No residents found");
      return;
    }

    setIsBackfilling(true);
    setError(null);
    setBackfillResults([]);

    const results = [];
    const BATCH_SIZE = 10; // Process in batches to avoid overwhelming Firestore

    try {
      for (let i = 0; i < allResidents.length; i += BATCH_SIZE) {
        const batch = allResidents.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (resident) => {
          const result = await updateResidentRiskFactors(resident);
          return {
            resident,
            ...result,
          };
        });

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);

        // Update progress
        setBackfillResults([...results]);

        // Small delay between batches
        if (i + BATCH_SIZE < allResidents.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    } catch (err) {
      setError(`Bulk backfill error: ${err.message}`);
    } finally {
      setIsBackfilling(false);
    }
  };

  // Count documents in the selected collection
  const countDocuments = async () => {
    if (!selectedOrg || !selectedCollection) return;

    try {
      setIsProcessing(true);
      const db = getFirestore();
      const collRef = collection(db, selectedCollection.path);
      const snapshot = await getDocs(collRef);

      setDocumentCount(snapshot.docs.length);

      // Preview first 5 documents to show what we're updating
      const previewDocs = snapshot.docs.slice(0, 5).map((doc) => ({
        id: doc.id,
        data: doc.data(),
      }));

      setUpdateResults({
        previewDocs,
        totalCount: snapshot.docs.length,
      });
    } catch (error) {
      console.error("Error counting documents:", error);
      setError(`Error counting documents: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Parse field value based on type
  const parseFieldValue = (value, type) => {
    switch (type) {
      case "boolean":
        return value === "true";
      case "number":
        return Number(value);
      case "array":
        try {
          return JSON.parse(value);
        } catch (e) {
          return value.split(",").map((item) => item.trim());
        }
      case "map":
        try {
          return JSON.parse(value);
        } catch (e) {
          return { value };
        }
      case "null":
        return null;
      case "timestamp":
        return new Date(value);
      default:
        return value;
    }
  };

  // Perform bulk update
  const performBulkUpdate = async () => {
    if (!selectedOrg || !selectedCollection || !fieldName) {
      setError("Please select an organization, collection, and field name");
      return;
    }

    if (!confirmUpdate) {
      setConfirmUpdate(true);
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      setUpdateSuccess(null);

      const db = getFirestore();
      const collRef = collection(db, selectedCollection.path);
      const snapshot = await getDocs(collRef);
      const totalDocs = snapshot.docs.length;

      if (totalDocs === 0) {
        setError("No documents found in this collection");
        setIsProcessing(false);
        return;
      }

      // Parse the field value according to its type
      const parsedValue = parseFieldValue(fieldValue, fieldType);

      // Perform updates in batches of 500 (Firestore batch limit)
      const BATCH_SIZE = 500;
      let successCount = 0;
      let failedCount = 0;
      let failedIds = [];

      for (let i = 0; i < totalDocs; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = snapshot.docs.slice(
          i,
          Math.min(i + BATCH_SIZE, totalDocs)
        );

        chunk.forEach((document) => {
          const docRef = doc(db, selectedCollection.path, document.id);
          batch.update(docRef, { [fieldName]: parsedValue });
        });

        try {
          await batch.commit();
          successCount += chunk.length;
        } catch (error) {
          console.error("Error in batch update:", error);
          failedCount += chunk.length;
          failedIds = [...failedIds, ...chunk.map((d) => d.id)];
        }
      }

      setUpdateSuccess({
        totalDocs,
        successCount,
        failedCount,
        failedIds: failedIds.slice(0, 10), // Only show first 10 failed IDs
      });

      setConfirmUpdate(false);
    } catch (error) {
      console.error("Update error:", error);
      setError(`Error updating documents: ${error.message}`);
      setConfirmUpdate(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to format dates
  function formatDate(dateStr) {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.warn(`Invalid date format: ${dateStr}`);
      return "";
    }
  }

  // Helper function to format phone numbers
  function formatPhoneNumber(phone) {
    if (!phone) return "";
    const phoneStr = String(phone).replace(/\D/g, "");
    if (phoneStr.length === 10) {
      return `${phoneStr.substring(0, 3)}-${phoneStr.substring(
        3,
        6
      )}-${phoneStr.substring(6)}`;
    }
    return phoneStr;
  }

  // Field transformers from the original component
  const fieldTransformers = {
    // Handle currentResident status
    currentResident: (value) => {
      if (typeof value === "boolean") return value;
      const activeStatuses = ["Active", "Current", "Enrolled"];
      return activeStatuses.includes(value);
    },

    // Format dates
    dob: formatDate,
    intakeDate: formatDate,
    dischargeDate: formatDate,
    sobrietyDate: formatDate,
    employmentStartDate: formatDate,
    employmentEndDate: formatDate,

    // Format phone numbers
    phoneNumber: formatPhoneNumber,
    ecPhoneNumber: formatPhoneNumber,

    // Generate fullName
    fullName: (value, record) => {
      if (value) return value;
      const first = record.firstName || "";
      const last = record.lastName || "";
      return `${first} ${last}`.trim();
    },

    // Email formatter (lowercase and trim)
    email: (value) => {
      if (!value) return "";
      return String(value).toLowerCase().trim();
    },

    // Split emergency contact name
    ecFirstName: (value, record) => {
      if (!value) return "";
      record._originalEcName = value;
      const parts = value.split(" ");
      return parts[0] || "";
    },

    ecLastName: (value, record) => {
      const fullName = record._originalEcName || "";
      const parts = fullName.split(" ");
      if (parts.length <= 1) return "";
      return parts.slice(1).join(" ");
    },

    // Map discharge status
    dischargeStatus: (value) => {
      if (!value) return "Unknown";
      const statusMap = {
        Successful: "Graduated",
        Unsuccessful: "Removed",
        "Left AMA": "AMA",
        "Non-Compliance": "Removed",
      };
      return statusMap[value] || "Unknown";
    },

    // Convert drug of choice
    drugOfChoice: (value) => {
      if (!value) return [];
      if (typeof value === "string") {
        return value.split(",").map((drug) => drug.trim());
      }
      return Array.isArray(value) ? value : [value];
    },
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (results) => {
        setCsvData(results.data);
        setIsProcessing(false);
      },
      error: (error) => {
        setError(`Error parsing CSV: ${error.message}`);
        setIsProcessing(false);
      },
    });
  };

  // Show original CSV sample
  const showCSVSample = () => {
    if (!csvData || csvData.length === 0) return null;

    // Get first record's keys for table headers
    const headers = Object.keys(csvData[0]);

    return (
      <div className="mt-4 mb-6 overflow-auto">
        <h3 className="text-lg font-medium mb-2">Original CSV Sample:</h3>
        <table className="min-w-full bg-white border border-gray-300 text-xs">
          <thead>
            <tr className="bg-gray-100">
              {headers.map((header) => (
                <th
                  key={header}
                  className="border border-gray-300 px-2 py-1 text-left"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {csvData.slice(0, 3).map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx % 2 === 0 ? "bg-gray-50" : ""}>
                {headers.map((header) => (
                  <td
                    key={`${rowIdx}-${header}`}
                    className="border border-gray-300 px-2 py-1"
                  >
                    {row[header] !== null ? String(row[header]) : ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Transform the data
  const transformData = () => {
    if (!csvData) {
      setError("Please upload a CSV file first");
      return;
    }

    try {
      // Transform each record
      const transformedRecords = csvData.map((sourceRecord, index) => {
        const targetRecord = { ...defaultValues };

        // Apply basic field mappings
        Object.entries(fieldMappings).forEach(([sourceField, targetField]) => {
          if (
            sourceField in sourceRecord &&
            sourceRecord[sourceField] !== null
          ) {
            targetRecord[targetField] = sourceRecord[sourceField];
          }
        });

        // Apply custom transformations
        Object.entries(fieldTransformers).forEach(([field, transformer]) => {
          if (field in targetRecord) {
            targetRecord[field] = transformer(
              targetRecord[field],
              targetRecord
            );
          }
        });

        // Generate an ID for the record
        targetRecord.id = `imported_${index}_${Date.now()}`;

        // Remove temporary fields
        Object.keys(targetRecord).forEach((key) => {
          if (key.startsWith("_")) {
            delete targetRecord[key];
          }
        });

        return targetRecord;
      });

      setResults({
        count: transformedRecords.length,
        preview: transformedRecords.slice(0, 5),
        allRecords: transformedRecords,
      });
    } catch (error) {
      console.error("Transform error:", error);
      setError(`Error transforming data: ${error.message}`);
    }
  };

  // Render a transformed record as a readable format
  const renderTransformedRecord = (record, index) => {
    // Fields to highlight (most important ones first)
    const mainFields = [
      "firstName",
      "lastName",
      "fullName",
      "dob",
      "phoneNumber",
      "house",
      "currentResident",
    ];
    const remainingFields = Object.keys(record).filter(
      (key) => !mainFields.includes(key)
    );

    return (
      <div key={index} className="mb-6 p-4 border rounded bg-white">
        <h4 className="font-bold text-lg mb-2">Record {index + 1}</h4>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {mainFields.map((field) => (
            <div key={field} className="mb-1">
              <span className="font-semibold">{field}:</span>{" "}
              <span className="text-blue-700">
                {typeof record[field] === "boolean"
                  ? String(record[field])
                  : Array.isArray(record[field])
                  ? JSON.stringify(record[field])
                  : record[field] || "-"}
              </span>
            </div>
          ))}
        </div>

        <details>
          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
            View all fields
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {remainingFields.map((field) => (
              <div key={field} className="mb-1">
                <span className="font-semibold">{field}:</span>{" "}
                <span>
                  {typeof record[field] === "boolean"
                    ? String(record[field])
                    : Array.isArray(record[field])
                    ? JSON.stringify(record[field])
                    : record[field] || "-"}
                </span>
              </div>
            ))}
          </div>
        </details>
      </div>
    );
  };

  // Upload to Firebase
  const uploadToFirebase = async () => {
    if (!selectedOrg) {
      setError("Please select an organization first");
      return;
    }

    if (!results || !results.allRecords) {
      setError("Please transform the data first");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const db = getFirestore();
      const records = results.allRecords;
      const BATCH_LIMIT = 400;

      // Create reference to the correct organization's residents collection
      const residentsCollectionPath = `organizations/${selectedOrg.id}/residents`;

      // Upload in batches
      for (let i = 0; i < records.length; i += BATCH_LIMIT) {
        const batch = writeBatch(db);
        const chunk = records.slice(i, i + BATCH_LIMIT);

        chunk.forEach((record) => {
          // Create a document reference in the selected organization's residents collection
          const docRef = doc(collection(db, residentsCollectionPath));

          // Set the generated ID back to the record
          record.id = docRef.id;
          batch.set(docRef, record);
        });

        await batch.commit();
        console.log(`Uploaded records ${i + 1} to ${i + chunk.length}`);
      }

      setIsProcessing(false);
      alert(
        `Successfully uploaded ${records.length} records to ${
          selectedOrg.name || selectedOrg.id
        }'s residents collection!`
      );
    } catch (error) {
      console.error("Upload error:", error);
      setError(`Error uploading to Firebase: ${error.message}`);
      setIsProcessing(false);
    }
  };

  // Render document preview for bulk update
  const renderDocumentPreview = (doc) => {
    return (
      <div className="mb-4 p-3 border rounded bg-gray-800 text-white">
        <h4 className="font-medium text-blue-400 mb-2">
          Document ID: {doc.id}
        </h4>
        <div className="text-xs text-gray-300 overflow-hidden overflow-ellipsis">
          {Object.entries(doc.data)
            .slice(0, 10)
            .map(([key, value]) => (
              <div key={key} className="mb-1">
                <span className="font-semibold text-gray-400">{key}:</span>{" "}
                <span>
                  {typeof value === "object"
                    ? JSON.stringify(value).substring(0, 50) +
                      (JSON.stringify(value).length > 50 ? "..." : "")
                    : String(value).substring(0, 50) +
                      (String(value).length > 50 ? "..." : "")}
                </span>
              </div>
            ))}
          {Object.keys(doc.data).length > 10 && (
            <div className="text-gray-500 italic">
              ...and {Object.keys(doc.data).length - 10} more fields
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-900 text-white">
      <Helmet>
        <title>Enhanced Migration Tool</title>
      </Helmet>

      <h1 className="text-3xl font-bold mb-6">
        Enhanced Firestore Management Tool
      </h1>

      {/* Tab Navigation */}
      <div className="flex mb-6 border-b border-gray-700">
        <button
          className={`px-4 py-2 flex items-center ${
            activeTab === "csv"
              ? "border-b-2 border-blue-500 text-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
          onClick={() => setActiveTab("csv")}
        >
          <FileText size={18} className="mr-2" />
          CSV Migration
        </button>
        <button
          className={`px-4 py-2 flex items-center ${
            activeTab === "backfill"
              ? "border-b-2 border-blue-500 text-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
          onClick={() => setActiveTab("backfill")}
        >
          <Settings size={18} className="mr-2" />
          Questionnaire Back‑fill
        </button>

        <button
          className={`px-4 py-2 flex items-center ${
            activeTab === "bulk"
              ? "border-b-2 border-blue-500 text-blue-400"
              : "text-gray-400 hover:text-white"
          }`}
          onClick={() => setActiveTab("bulk")}
        >
          <Database size={18} className="mr-2" />
          Bulk Field Updates
        </button>
      </div>

      {error && (
        <div className="bg-red-900 border border-red-600 text-white px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Organization Selection - Common for both tabs */}
      <div className="mb-8 p-4 border border-gray-700 rounded">
        <h2 className="text-xl font-semibold mb-3">
          Select Target Organization
        </h2>

        {loadingOrgs ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-slate-400">Loading organizations...</p>
          </div>
        ) : (
          <>
            <div className="relative">
              <select
                value={selectedOrg?.id || ""}
                onChange={(e) => {
                  const selected = organizations.find(
                    (org) => org.id === e.target.value
                  );
                  setSelectedOrg(selected);
                  // Reset other selections when org changes
                  setSelectedCollection(null);
                  setUpdateResults(null);
                  setDocumentCount(0);
                }}
                className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 appearance-none border border-slate-700"
              >
                <option value="" disabled>
                  Select an organization
                </option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name || org.id}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-3 top-2.5 text-slate-400 pointer-events-none"
                size={18}
              />
            </div>

            {selectedOrg && (
              <div className="mt-3 p-3 bg-slate-800 rounded-lg">
                <p className="text-green-400 font-medium">
                  Selected: {selectedOrg.name || selectedOrg.id}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Back‑fill Tab */}
      {activeTab === "backfill" && (
        <>
          {/* Backfill Mode Selection */}
          <div className="mb-8 p-4 border border-gray-700 rounded">
            <h2 className="text-xl font-semibold mb-3">
              Backfill Tool Selection
            </h2>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="backfillToolMode"
                  value="manual"
                  checked={backfillMode === "manual"}
                  onChange={(e) => setBackfillMode(e.target.value)}
                  className="mr-2"
                />
                <span>Manual Update (Your existing tool)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="backfillToolMode"
                  value="auto"
                  checked={backfillMode === "auto"}
                  onChange={(e) => setBackfillMode(e.target.value)}
                  className="mr-2"
                />
                <span>Automatic Risk Factor Sync</span>
              </label>
            </div>
            <p className="text-gray-400 text-sm mt-2">
              {backfillMode === "manual"
                ? "Manually set specific values for a resident's questionnaires"
                : "Automatically sync risk factors from questionnaires to resident profiles"}
            </p>
          </div>

          {/* EXISTING MANUAL TOOL - Only show when manual mode is selected */}
          {backfillMode === "manual" && (
            <>
              {/* Resident picker */}
              <div className="mb-8 p-4 border border-gray-700 rounded">
                <h2 className="text-xl font-semibold mb-3">
                  Step 1: Select Resident
                </h2>
                {!selectedOrg ? (
                  <p className="text-yellow-400">
                    Select an organization first
                  </p>
                ) : residents.length === 0 ? (
                  <p className="text-gray-400">No residents found</p>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedResident?.id || ""}
                      onChange={(e) =>
                        setSelectedResident(
                          residents.find((r) => r.id === e.target.value)
                        )
                      }
                      className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 appearance-none border border-slate-700"
                    >
                      <option value="" disabled>
                        Select a resident
                      </option>
                      {residents.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.fullName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="absolute right-3 top-2.5 text-slate-400 pointer-events-none"
                      size={18}
                    />
                  </div>
                )}
              </div>

              {/* Field values */}
              <div className="mb-8 p-4 border border-gray-700 rounded">
                <h2 className="text-xl font-semibold mb-3">
                  Step 2: Choose Yes/No Values
                </h2>
                {[
                  "homelessOneYear",
                  "childhoodTrauma",
                  "twoParentsHome",
                  "coOccurringDiagnosis",
                ].map((key) => (
                  <div key={key} className="mb-4">
                    <label className="block text-gray-300 mb-2">
                      {key.replace(/([A-Z])/g, " $1")}
                    </label>
                    <select
                      value={boolFields[key]}
                      onChange={(e) =>
                        setBoolFields((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 border border-slate-700 appearance-none"
                    >
                      <option value="">Leave unchanged</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                ))}
              </div>

              {/* Execute */}
              <div className="mb-8 p-4 border border-gray-700 rounded">
                <h2 className="text-xl font-semibold mb-3">
                  Step 3: Run Back‑fill
                </h2>
                <button
                  onClick={runBackfill}
                  disabled={isUpdating}
                  className={`px-4 py-2 rounded font-semibold ${
                    isUpdating
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {isUpdating ? "Updating..." : "Update All Questionnaires"}
                </button>
                {updateSuccess && (
                  <p className="mt-3 text-green-400">
                    Updated {updateSuccess.backfilled} documents!
                  </p>
                )}
              </div>
            </>
          )}

          {/* NEW: AUTOMATIC RISK FACTOR SYNC TOOL */}
          {backfillMode === "auto" && (
            <>
              {/* Processing Mode Selection */}
              <div className="mb-8 p-4 border border-gray-700 rounded">
                <h2 className="text-xl font-semibold mb-3">Processing Mode</h2>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="processingMode"
                      value="single"
                      checked={processingMode === "single"}
                      onChange={(e) => setProcessingMode(e.target.value)}
                      className="mr-2"
                    />
                    <span>Single Resident</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="processingMode"
                      value="bulk"
                      checked={processingMode === "bulk"}
                      onChange={(e) => setProcessingMode(e.target.value)}
                      className="mr-2"
                    />
                    <span>All Residents (Bulk)</span>
                  </label>
                </div>
              </div>

              {/* Single Resident Mode */}
              {processingMode === "single" && (
                <div className="mb-8 p-4 border border-gray-700 rounded">
                  <h2 className="text-xl font-semibold mb-3">
                    Select Resident
                  </h2>
                  {!selectedOrg ? (
                    <p className="text-yellow-400">
                      Select an organization first
                    </p>
                  ) : allResidents.length === 0 ? (
                    <p className="text-gray-400">Loading residents...</p>
                  ) : (
                    <div className="relative">
                      <select
                        value={selectedResident?.id || ""}
                        onChange={(e) => {
                          const resident = allResidents.find(
                            (r) => r.id === e.target.value
                          );
                          setSelectedResident(resident);
                        }}
                        className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 appearance-none border border-slate-700"
                      >
                        <option value="" disabled>
                          Select a resident
                        </option>
                        {allResidents.map((r) => (
                          <option key={`${r.collection}-${r.id}`} value={r.id}>
                            {r.fullName} (
                            {r.collection === "alumni" ? "Alumni" : "Current"})
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        className="absolute right-3 top-2.5 text-slate-400 pointer-events-none"
                        size={18}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Bulk Mode Info */}
              {processingMode === "bulk" && (
                <div className="mb-8 p-4 border border-gray-700 rounded">
                  <h2 className="text-xl font-semibold mb-3">
                    Bulk Processing Info
                  </h2>
                  <p className="text-gray-300 mb-2">
                    This will process all residents and alumni for the selected
                    organization.
                  </p>
                  {allResidents.length > 0 && (
                    <>
                      <p className="text-blue-400">
                        Found {allResidents.length} total people to process:
                      </p>
                      <ul className="text-sm text-gray-400 mt-2">
                        <li>
                          • Current Residents:{" "}
                          {
                            allResidents.filter(
                              (r) => r.collection === "residents"
                            ).length
                          }
                        </li>
                        <li>
                          • Alumni:{" "}
                          {
                            allResidents.filter(
                              (r) => r.collection === "alumni"
                            ).length
                          }
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              )}

              {/* Execute Automatic Sync */}
              <div className="mb-8 p-4 border border-gray-700 rounded">
                <h2 className="text-xl font-semibold mb-3">
                  Execute Risk Factor Sync
                </h2>
                <p className="text-gray-300 mb-4">
                  This will automatically find each resident's most recent
                  Recovery Capital questionnaire and update their profile with
                  these risk factors:
                </p>
                <ul className="text-blue-400 text-sm mb-4 list-disc list-inside">
                  <li>overOneYearInPrison</li>
                  <li>homelessOneYear</li>
                  <li>childhoodTrauma</li>
                  <li>twoParentsHome</li>
                  <li>coOccurringDiagnosis</li>
                </ul>

                <button
                  onClick={
                    processingMode === "single"
                      ? backfillSingleResident
                      : backfillAllResidents
                  }
                  disabled={
                    isBackfilling ||
                    (!selectedResident && processingMode === "single") ||
                    allResidents.length === 0
                  }
                  className={`px-4 py-2 rounded font-semibold ${
                    isBackfilling ||
                    (!selectedResident && processingMode === "single") ||
                    allResidents.length === 0
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {isBackfilling
                    ? "Processing..."
                    : processingMode === "single"
                    ? "Sync Selected Resident"
                    : "Sync All Residents"}
                </button>
              </div>

              {/* Results Display */}
              {backfillResults && backfillResults.length > 0 && (
                <div className="mb-8 p-4 border border-gray-700 rounded">
                  <h2 className="text-xl font-semibold mb-3">Sync Results</h2>

                  <div className="mb-4">
                    <p className="text-green-400">
                      Processed: {backfillResults.length} residents
                    </p>
                    <p className="text-green-400">
                      Successfully updated:{" "}
                      {backfillResults.filter((r) => r.success).length}
                    </p>
                    <p className="text-yellow-400">
                      Failed/No data:{" "}
                      {backfillResults.filter((r) => !r.success).length}
                    </p>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {backfillResults.map((result, index) => (
                      <div
                        key={index}
                        className={`mb-3 p-3 rounded border ${
                          result.success
                            ? "bg-green-900/30 border-green-700"
                            : "bg-red-900/30 border-red-700"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">
                              {result.resident.fullName}
                              <span className="text-sm text-gray-400 ml-2">
                                ({result.resident.collection})
                              </span>
                            </p>

                            {result.success ? (
                              <div className="text-sm text-green-400 mt-1">
                                <p>
                                  Updated from questionnaire:{" "}
                                  {result.questionnaire}
                                </p>
                                <p>
                                  Fields updated:{" "}
                                  {Object.keys(result.updatedFields).join(", ")}
                                </p>
                                <div className="text-xs text-gray-400 mt-1">
                                  {Object.entries(result.updatedFields).map(
                                    ([key, value]) => (
                                      <span key={key} className="mr-3">
                                        {key}: {value}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-red-400 mt-1">
                                Failed: {result.reason}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* CSV Migration Tab */}
      {activeTab === "csv" && (
        <>
          <div className="mb-8 p-4 border border-gray-700 rounded">
            <h2 className="text-xl font-semibold mb-3">
              Step 1: Select CSV File
            </h2>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="block w-full text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-800 file:text-white hover:file:bg-blue-700"
            />
            {csvData && (
              <div className="mt-3 text-green-400 font-medium">
                Loaded {csvData.length} records from CSV
              </div>
            )}

            {csvData && csvData.length > 0 && showCSVSample()}
          </div>

          <div className="mb-8 p-4 border border-gray-700 rounded">
            <h2 className="text-xl font-semibold mb-3">
              Step 2: Transform Data
            </h2>
            <div className="mb-4">
              <p className="text-gray-400 mb-3">
                This will map the CSV fields to your Firebase fields according
                to these rules:
              </p>
              <div className="grid grid-cols-2 gap-2 mb-4 bg-gray-800 p-3 rounded text-sm">
                {Object.entries(fieldMappings).map(([source, target]) => (
                  <div key={source} className="flex items-center">
                    <span className="text-gray-400">{source}</span>
                    <span className="mx-2">→</span>
                    <span className="text-blue-400">{target}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={transformData}
              disabled={!csvData || isProcessing}
              className={`px-4 py-2 rounded font-semibold ${
                !csvData || isProcessing
                  ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {isProcessing ? "Uploading..." : "Upload to Firebase"}
            </button>
          </div>
        </>
      )}

      {/* Bulk Field Update Tab */}
      {activeTab === "bulk" && (
        <>
          {/* Collection Selection */}
          <div className="mb-8 p-4 border border-gray-700 rounded">
            <h2 className="text-xl font-semibold mb-3">
              Step 1: Select Collection
            </h2>
            {!selectedOrg ? (
              <p className="text-yellow-400">
                Please select an organization first
              </p>
            ) : loadingCollections ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-slate-400">Loading collections...</p>
              </div>
            ) : (
              <>
                {collections.length === 0 ? (
                  <p className="text-yellow-400">
                    No collections found for this organization
                  </p>
                ) : (
                  <>
                    <div className="relative mb-4">
                      <select
                        value={selectedCollection?.id || ""}
                        onChange={(e) => {
                          const selected = collections.find(
                            (col) => col.id === e.target.value
                          );
                          setSelectedCollection(selected);
                          setDocumentCount(0);
                          setUpdateResults(null);
                        }}
                        className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 appearance-none border border-slate-700"
                      >
                        <option value="" disabled>
                          Select a collection
                        </option>
                        {collections.map((col) => (
                          <option key={col.id} value={col.id}>
                            {col.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        className="absolute right-3 top-2.5 text-slate-400 pointer-events-none"
                        size={18}
                      />
                    </div>

                    {selectedCollection && (
                      <div className="p-3 bg-slate-800 rounded-lg">
                        <p className="text-green-400 font-medium">
                          Selected: {selectedCollection.name}
                        </p>
                        <p className="text-slate-400 text-sm">
                          Collection path:{" "}
                          <span className="text-blue-400 font-mono">
                            {selectedCollection.path}
                          </span>
                        </p>
                        <button
                          onClick={countDocuments}
                          disabled={isProcessing}
                          className={`mt-2 px-3 py-1 rounded text-sm ${
                            isProcessing
                              ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                        >
                          {isProcessing
                            ? "Loading..."
                            : "Count Documents & Preview"}
                        </button>

                        {documentCount > 0 && (
                          <p className="mt-2 text-white font-medium">
                            Found {documentCount} documents in this collection
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Field Definition */}
          <div className="mb-8 p-4 border border-gray-700 rounded">
            <h2 className="text-xl font-semibold mb-3">
              Step 2: Define Field to Add/Update
            </h2>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Field Name:</label>
              <input
                type="text"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                placeholder="e.g. isActive, status, lastUpdated"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Field Type:</label>
              <div className="relative">
                <select
                  value={fieldType}
                  onChange={(e) => {
                    setFieldType(e.target.value);
                    // Reset field value when type changes
                    setFieldValue("");
                  }}
                  className="w-full bg-slate-800 text-white rounded-lg px-3 py-2 appearance-none border border-slate-700"
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="array">Array</option>
                  <option value="map">Object/Map</option>
                  <option value="timestamp">Timestamp</option>
                  <option value="null">Null</option>
                </select>
                <ChevronDown
                  className="absolute right-3 top-2.5 text-slate-400 pointer-events-none"
                  size={18}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">
                Field Value:
                {fieldType === "boolean" && (
                  <span className="text-slate-400 text-sm ml-2">
                    (true or false)
                  </span>
                )}
                {fieldType === "array" && (
                  <span className="text-slate-400 text-sm ml-2">
                    (comma-separated values or JSON array)
                  </span>
                )}
                {fieldType === "map" && (
                  <span className="text-slate-400 text-sm ml-2">
                    (JSON object)
                  </span>
                )}
                {fieldType === "timestamp" && (
                  <span className="text-slate-400 text-sm ml-2">
                    (YYYY-MM-DD or ISO format)
                  </span>
                )}
              </label>

              {fieldType === "boolean" ? (
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="booleanValue"
                      value="true"
                      checked={fieldValue === "true"}
                      onChange={() => setFieldValue("true")}
                      className="mr-2"
                    />
                    <span>true</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="booleanValue"
                      value="false"
                      checked={fieldValue === "false"}
                      onChange={() => setFieldValue("false")}
                      className="mr-2"
                    />
                    <span>false</span>
                  </label>
                </div>
              ) : fieldType === "null" ? (
                <p className="text-slate-400">
                  Field will be set to null (no value needed)
                </p>
              ) : (
                <input
                  type={fieldType === "number" ? "number" : "text"}
                  value={fieldValue}
                  onChange={(e) => setFieldValue(e.target.value)}
                  placeholder={
                    fieldType === "array"
                      ? 'e.g. "item1, item2, item3" or ["item1","item2"]'
                      : fieldType === "map"
                      ? 'e.g. {"key": "value"}'
                      : fieldType === "timestamp"
                      ? "YYYY-MM-DD"
                      : "Enter value"
                  }
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              )}
            </div>
          </div>

          {/* Document Preview */}
          {updateResults && updateResults.previewDocs && (
            <div className="mb-8 p-4 border border-gray-700 rounded">
              <h2 className="text-xl font-semibold mb-3">
                Document Preview (Sample of {updateResults.previewDocs.length})
              </h2>
              <p className="text-gray-400 mb-4">
                Showing {updateResults.previewDocs.length} out of{" "}
                {updateResults.totalCount} documents that will be updated
              </p>

              <div className="bg-gray-800 p-4 rounded overflow-auto max-h-[400px]">
                {updateResults.previewDocs.map((doc) =>
                  renderDocumentPreview(doc)
                )}
              </div>
            </div>
          )}

          {/* Update Execution */}
          <div className="mb-8 p-4 border border-gray-700 rounded">
            <h2 className="text-xl font-semibold mb-3">
              Step 3: Execute Bulk Update
            </h2>

            {!selectedCollection || !documentCount ? (
              <p className="text-yellow-400">
                Please select a collection and count documents first
              </p>
            ) : !fieldName ? (
              <p className="text-yellow-400">Please enter a field name</p>
            ) : fieldType !== "null" &&
              !fieldValue &&
              fieldType !== "boolean" ? (
              <p className="text-yellow-400">Please enter a field value</p>
            ) : (
              <>
                <div className="bg-slate-800 p-3 rounded mb-4 border border-slate-700">
                  <p className="mb-2">
                    You are about to perform the following update:
                  </p>
                  <ul className="list-disc list-inside text-blue-400 mb-2">
                    <li>
                      Add/update field{" "}
                      <span className="font-mono">{fieldName}</span>
                    </li>
                    <li>
                      With value:{" "}
                      <span className="font-mono">
                        {fieldType === "null"
                          ? "null"
                          : fieldType === "boolean"
                          ? fieldValue
                          : fieldValue || "(empty)"}
                      </span>
                    </li>
                    <li>
                      To <span className="font-bold">{documentCount}</span>{" "}
                      documents in collection{" "}
                      <span className="font-mono">
                        {selectedCollection.path}
                      </span>
                    </li>
                  </ul>
                  <p className="text-yellow-400 text-sm mt-3">
                    Warning: This operation cannot be undone!
                  </p>
                </div>

                <button
                  onClick={performBulkUpdate}
                  disabled={isProcessing}
                  className={`px-4 py-2 rounded font-semibold ${
                    isProcessing
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : confirmUpdate
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-yellow-600 text-white hover:bg-yellow-700"
                  }`}
                >
                  {isProcessing
                    ? "Processing..."
                    : confirmUpdate
                    ? "Confirm Bulk Update"
                    : "Perform Bulk Update"}
                </button>

                {confirmUpdate && (
                  <button
                    onClick={() => setConfirmUpdate(false)}
                    className="px-4 py-2 ml-3 rounded font-semibold bg-gray-600 text-white hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                )}

                {updateSuccess && (
                  <div className="mt-4 p-3 bg-green-900 text-white rounded border border-green-700">
                    <h3 className="font-medium mb-2">Update Complete!</h3>
                    <p>Total documents processed: {updateSuccess.totalDocs}</p>
                    <p>Successfully updated: {updateSuccess.successCount}</p>
                    {updateSuccess.failedCount > 0 && (
                      <>
                        <p className="text-red-400">
                          Failed: {updateSuccess.failedCount}
                        </p>
                        {updateSuccess.failedIds.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm">
                              Some document IDs that failed:
                            </p>
                            <ul className="text-xs font-mono">
                              {updateSuccess.failedIds.map((id, idx) => (
                                <li key={idx}>{id}</li>
                              ))}
                            </ul>
                            {updateSuccess.failedIds.length <
                              updateSuccess.failedCount && (
                              <p className="text-xs text-gray-400 mt-1">
                                ...and{" "}
                                {updateSuccess.failedCount -
                                  updateSuccess.failedIds.length}{" "}
                                more
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EnhancedMigrationTool;
